/**
 * The people and the things that walk around.
 *
 * Everything alive is one of a handful of models, all built on the same skeleton
 * and carrying the same set of animations, so a body is a model plus a few
 * colours plus whichever clip suits what it is doing. The models are loaded once
 * and cloned per body, which is what keeps a crowd cheap: one download, one set
 * of geometry, one skeleton copied per person.
 *
 * Colours are set on cloned materials rather than baked into a texture, so
 * somebody choosing their skin at the character screen is a number on a material
 * and nothing else.
 */

import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { clone as cloneSkinned } from 'three/examples/jsm/utils/SkeletonUtils.js';

/**
 * What a body is doing, which is all the animation needs to know.
 *
 * All but one of these is a clip out of the model pack. `bend` is ours: the
 * pack has nothing for somebody working with both hands at something on the
 * ground, so it is made here out of the bones themselves.
 */
export type Pose = 'idle' | 'walk' | 'run' | 'hit' | 'die' | 'pickup' | 'bend';

/** The clip each pose is played from. Every model in the pack carries all of them. */
const CLIPS: Record<Exclude<Pose, 'bend'>, string> = {
  idle: 'Idle',
  walk: 'Walk',
  run: 'Run',
  hit: 'Punch',
  die: 'Death',
  /** Stooping to take something off the ground, which is what it was made for. */
  pickup: 'PickUp',
};

/** Poses that finish rather than loop. */
const ONCE: Pose[] = ['hit', 'die', 'pickup'];

/**
 * What the world says each pose should be played from, where it says anything.
 *
 * The names above are what the engine was built with. A clip authored later and
 * pointed at a pose in the tools lands here instead, so new work reaches the
 * bodies without anybody touching this file. A pose nothing is written for
 * keeps the built-in name.
 */
const written = new Map<string, { clip: string; loops: boolean; speed: number }>();

export function usePoses(
  poses: readonly { pose: string; clip: string; loops: boolean; speed: number }[],
): void {
  written.clear();
  for (const held of poses) {
    written.set(held.pose, { clip: held.clip, loops: held.loops, speed: held.speed });
  }
}

/** What a pose is played from now: what was written for it, or the built-in. */
function clipFor(pose: Exclude<Pose, 'bend'>): {
  clip: string;
  loops: boolean;
  speed: number;
} {
  const held = written.get(pose);
  if (held) return held;

  return { clip: CLIPS[pose], loops: !ONCE.includes(pose), speed: 1 };
}

/**
 * The bend: down over the work, both hands to it, and back up.
 *
 * Times in seconds, from the moment it starts. Down quickly, a moment held
 * there doing whatever it is, and a slower rise, which is how somebody who has
 * been crouching over a fire actually stands up.
 */
const BEND = { down: 0.35, held: 0.35, up: 0.55 };

/**
 * Where the bend is aiming is measured from the pose the model was made in.
 *
 * Added to whatever the idle clip was doing, the arms picked up the idle's own
 * swing and waved about; the legs picked up its weight shift and walked
 * themselves backwards. A fixed pose to turn towards, taken from the rest pose
 * once, gives one still and deliberate movement instead, and the clip
 * underneath goes on doing whatever it likes without showing through.
 *
 * Getting there is a different question, and the answer is in `applyBend`: the
 * turn starts wherever the clip has the bone now, so nothing jumps at either
 * end of it.
 */

/**
 * How far each bone turns at the bottom of it, in radians, from its rest pose.
 *
 * Named the way the loader names them, which is the way the model was made with
 * the dots taken out: UpperArmL rather than UpperArm.L. The legs are left out
 * on purpose: feet stay where they were put, and a bend that shuffles somebody
 * backwards reads as a stumble.
 */
const BENT: Readonly<Record<string, { x?: number; y?: number; z?: number }>> = {
  Abdomen: { x: 0.72 },
  Torso: { x: 0.44 },
  Neck: { x: -0.28 },
  // The arms are not aligned with the body's axes, so these were measured
  // rather than guessed: at these angles the fists end up a quarter of a metre
  // in front of the shoulders and about a shoulder's width apart, which is
  // somebody working at something on the ground rather than somebody praying.
  // Turning the other way put the arms behind the back.
  UpperArmL: { x: 0.85, z: -0.2 },
  UpperArmR: { x: 0.85, z: 0.2 },
  LowerArmL: { x: -0.5 },
  LowerArmR: { x: -0.5 },
};

export interface Appearance {
  /** Skin, as a hex number. The model's own skin is very dark, and most people change it. */
  skin: number;
  /** The eyes and brows, which are the only other thing painted on the face. */
  eyes: number;
}

export const DEFAULT_APPEARANCE: Appearance = { skin: 0x6b4a35, eyes: 0xf2efe6 };

/**
 * The models there are, and the file behind each.
 *
 * One skeleton and one set of animations across all of them, which is what makes
 * a name enough: anything here can be given to anybody and will walk, fight and
 * fall over like everybody else.
 */
const MODELS = {
  person: 'BaseCharacter',
  casual2Female: 'Casual2_Female',
  casual2Male: 'Casual2_Male',
  casualFemale: 'Casual_Female',
  casualMale: 'Casual_Male',
  cow: 'Cow',
  elf: 'Elf',
  goblinFemale: 'Goblin_Female',
  goblinMale: 'Goblin_Male',
  knightMale: 'Knight_Male',
  pirateFemale: 'Pirate_Female',
  pirateMale: 'Pirate_Male',
  pug: 'Pug',
  soldierFemale: 'Soldier_Female',
  soldierMale: 'Soldier_Male',
  witch: 'Witch',
  wizard: 'Wizard',
  workerFemale: 'Worker_Female',
  workerMale: 'Worker_Male',
  zombieFemale: 'Zombie_Female',
  zombieMale: 'Zombie_Male',
} as const;

export type ModelName = keyof typeof MODELS;

/**
 * How large the models are drawn.
 *
 * The pack is built at roughly a metre and a half to the unit, and the world is
 * a tile to the unit, so everything comes in about right at a little under one.
 */
export const MODEL_SCALE = 0.55;

/** Every model that can be asked for, for the tools that offer a choice of one. */
export const MODEL_NAMES = Object.keys(MODELS) as ModelName[];

/** Whether there is a model by this name, for anything read off the wire. */
export function modelExists(name: string): name is ModelName {
  return name in MODELS;
}

/** The file behind a name, falling back to a person for anything unknown. */
function fileOf(name: ModelName): string {
  return MODELS[name] ?? MODELS.person;
}

interface Loaded {
  scene: THREE.Group;
  clips: THREE.AnimationClip[];
}

const loader = new GLTFLoader();
const loading = new Map<string, Promise<Loaded>>();
const ready = new Map<string, Loaded>();

/**
 * Fetches a model, once.
 *
 * Everything that asks for the same model while it is still coming down waits on
 * the same promise, so a field full of the same enemy is still one request.
 */
/**
 * Every clip the bodies already loaded are carrying, by name.
 *
 * For the tools: what a pose may be pointed at is whatever the model actually
 * holds, and a list typed by hand is a list that goes stale the first time
 * somebody exports again.
 */
export function clipsLoaded(): string[] {
  const names = new Set<string>();
  for (const loaded of ready.values()) {
    for (const clip of loaded.clips) names.add(clip.name);
  }

  return [...names].sort((a, b) => a.localeCompare(b));
}

export function loadModel(name: ModelName): Promise<Loaded> {
  const file = fileOf(name);
  const held = loading.get(file);
  if (held) return held;

  const started = new Promise<Loaded>((resolve, reject) => {
    loader.load(
      `models/${file}.gltf`,
      (gltf) => {
        const loaded = { scene: gltf.scene, clips: gltf.animations };
        ready.set(file, loaded);
        resolve(loaded);
      },
      undefined,
      (error) => reject(error instanceof Error ? error : new Error(String(error))),
    );
  });

  loading.set(file, started);
  return started;
}

/**
 * Fetches a model somebody brought in, by the file it was written to.
 *
 * The same one-request rule as the pack: everything that wants it while it is
 * coming down waits on the same promise. Kept apart from the pack's own loader
 * because these are named by file rather than by a name in a list, and because
 * they are static props rather than bodies with a skeleton.
 */
export function loadUploaded(file: string): Promise<Loaded> {
  const key = `uploaded/${file}`;
  const held = loading.get(key);
  if (held) return held;

  const started = new Promise<Loaded>((resolve, reject) => {
    loader.load(
      `models/uploaded/${file}`,
      (gltf) => {
        const loaded = { scene: gltf.scene, clips: gltf.animations };
        ready.set(key, loaded);
        resolve(loaded);
      },
      undefined,
      (error) => reject(error instanceof Error ? error : new Error(String(error))),
    );
  });

  loading.set(key, started);
  return started;
}

/** A model already in hand, for the frame that cannot wait for one. */
export function loadedModel(name: ModelName): Loaded | null {
  return ready.get(fileOf(name)) ?? null;
}

/**
 * One body: what is drawn, and what it is doing.
 *
 * Poses cross fade rather than cut, which for a quarter of a second of overlap
 * is the difference between somebody walking and somebody being teleported into
 * a walking shape.
 */
export class Avatar {
  readonly group = new THREE.Group();

  private mixer: THREE.AnimationMixer | null = null;
  private actions = new Map<Pose, THREE.AnimationAction>();
  private playing: Pose | null = null;

  /** While a one-shot clip is still playing, and nothing quiet may interrupt it. */
  private holdingUntil = 0;

  /** The bones the bend turns, each with where it starts and where it goes. */
  private readonly bones = new Map<
    string,
    { bone: THREE.Bone; rest: THREE.Quaternion; bent: THREE.Quaternion }
  >();

  private bendingSince = 0;
  private skins: THREE.MeshStandardMaterial[] = [];
  private faces: THREE.MeshStandardMaterial[] = [];
  private held: Appearance = { ...DEFAULT_APPEARANCE };

  /** Set once the model is in and the body is really there rather than a placeholder. */
  dressed = false;

  /**
   * The skeleton this body is built on, and how its mesh was bound to it.
   *
   * Kept because every model in the pack is built on the same twenty three
   * bones in the same order, which means a piece of one model can be worn by
   * another: a hat is a lump of geometry weighted to the head bone, and the
   * head bone is the head bone everywhere.
   */
  private skeleton: THREE.Skeleton | null = null;
  private bindMatrix: THREE.Matrix4 | null = null;

  constructor(
    private readonly model: ModelName,
    appearance: Appearance,
    private readonly scale: number,
  ) {
    this.held = { ...appearance };

    const already = loadedModel(model);
    if (already) {
      this.build(already);
      return;
    }

    void loadModel(model).then((loaded) => {
      if (!this.disposed) this.build(loaded);
    });
  }

  private disposed = false;

  private build(loaded: Loaded): void {
    const body = cloneSkinned(loaded.scene);
    body.scale.setScalar(this.scale);

    body.traverse((child) => {
      if (!(child instanceof THREE.Mesh)) return;
      child.castShadow = false;
      child.receiveShadow = false;

      // Cloning the skeleton shares the materials, and two people would then be
      // painted the same colour by whichever of them was dressed last.
      const materials = Array.isArray(child.material) ? child.material : [child.material];
      const copies = materials.map((material) => {
        const copy = (material as THREE.Material).clone() as THREE.MeshStandardMaterial;
        if (copy.name === 'Skin') this.skins.push(copy);
        if (copy.name === 'Face') this.faces.push(copy);
        return copy;
      });

      child.material = Array.isArray(child.material) ? copies : (copies[0] as THREE.Material);
    });

    // The skeleton this body is on, so a piece of another model can be bound to
    // it later and worn.
    body.traverse((child) => {
      if (child instanceof THREE.SkinnedMesh && !this.skeleton) {
        this.skeleton = child.skeleton;
        this.bindMatrix = child.bindMatrix.clone();
      }
    });

    this.group.add(body);

    // The bones the bend turns, found by name once rather than by walking the
    // skeleton every frame, each with the pose it was modelled in and the pose
    // the bend wants it in.
    body.traverse((child) => {
      if (!(child instanceof THREE.Bone)) return;

      const turn = BENT[child.name];
      if (!turn) return;

      const bone = child as THREE.Bone;
      const rest = bone.quaternion.clone();
      const bent = rest
        .clone()
        .multiply(
          new THREE.Quaternion().setFromEuler(
            new THREE.Euler(turn.x ?? 0, turn.y ?? 0, turn.z ?? 0),
          ),
        );

      this.bones.set(bone.name, { bone, rest, bent });
    });

    this.mixer = new THREE.AnimationMixer(body);
    for (const pose of Object.keys(CLIPS) as Exclude<Pose, 'bend'>[]) {
      const wanted = clipFor(pose);

      // What was written for it, and the built-in name as a fallback: a clip
      // pointed at a name this model does not carry should leave the body
      // moving the way it did rather than standing frozen.
      const clip =
        loaded.clips.find((held) => held.name === wanted.clip) ??
        loaded.clips.find((held) => held.name === CLIPS[pose]);
      if (!clip) continue;

      const action = this.mixer.clipAction(clip);
      action.setEffectiveTimeScale(wanted.speed);

      if (!wanted.loops) {
        action.setLoop(THREE.LoopOnce, 1);
        action.clampWhenFinished = true;
      }

      this.actions.set(pose, action);
    }

    this.dressed = true;
    this.wear(this.held);
    this.pose(this.playing ?? 'idle', true);
  }

  /**
   * Paints the skin and the face.
   *
   * Colours of zero mean the model keeps its own paint, which is how everything
   * that is not a player character is drawn: a goblin was made green by the
   * person who made it and nobody chooses that at a character screen.
   */
  wear(appearance: Appearance): void {
    this.held = { ...appearance };
    if (appearance.skin > 0)
      for (const material of this.skins) material.color.setHex(appearance.skin);
    if (appearance.eyes > 0)
      for (const material of this.faces) material.color.setHex(appearance.eyes);
  }

  pose(pose: Pose, immediately = false): void {
    // The bend is not a clip: it is laid over whichever clip is running, so it
    // starts and the body carries on doing what it was doing underneath.
    if (pose === 'bend') {
      this.bendingSince = performance.now();
      return;
    }

    // Walking is the end of a bend. Standing still is not: somebody stays down
    // over the fire between turns of the fish.
    if (pose === 'walk' || pose === 'run') this.bendingSince = 0;

    // A clip that plays once holds the body until it has finished. Without this
    // the very next frame asks for idle again and the reach is over before the
    // hands have moved. Walking still breaks it: somebody who set off is not
    // standing there reaching into the water any more.
    const holding = performance.now() < this.holdingUntil;
    if (holding && (pose === 'idle' || pose === this.playing)) return;

    if (this.playing === pose && !immediately) return;

    const next = this.actions.get(pose);
    if (!next) {
      this.playing = pose;
      this.holdingUntil = 0;
      return;
    }

    this.holdingUntil = ONCE.includes(pose)
      ? performance.now() + next.getClip().duration * 1000
      : 0;

    const previous = this.playing ? this.actions.get(this.playing) : undefined;
    this.playing = pose;

    next.reset();
    next.enabled = true;
    next.setEffectiveWeight(1);
    next.play();

    if (previous && previous !== next && !immediately) next.crossFadeFrom(previous, 0.22, true);
    else if (previous && previous !== next) previous.stop();
  }

  update(deltaSeconds: number): void {
    this.mixer?.update(deltaSeconds);
    this.applyBend();
  }

  /**
   * The bend, laid over whatever the mixer has just done.
   *
   * Added to the bones rather than replacing them, so somebody bending over a
   * fire still breathes: the idle underneath keeps playing and this leans it
   * forward. The mixer writes the bones fresh every frame, so adding to them is
   * safe and needs nothing put back afterwards.
   */
  private applyBend(): void {
    if (this.bendingSince === 0) return;

    const age = (performance.now() - this.bendingSince) / 1000;
    const whole = BEND.down + BEND.held + BEND.up;
    if (age >= whole) {
      this.bendingSince = 0;
      return;
    }

    // Eased at both ends, so nobody snaps into a bow.
    const ease = (value: number): number => value * value * (3 - 2 * value);
    const strength =
      age < BEND.down
        ? ease(age / BEND.down)
        : age < BEND.down + BEND.held
          ? 1
          : ease(1 - (age - BEND.down - BEND.held) / BEND.up);

    // Turned from wherever the clip has just put the bone, not from the pose the
    // model was made in. Starting from the rest pose pinned the bones there for
    // the whole bend and then handed them back to the clip in one frame, which
    // is the jolt at the end of it. Leaning out of the live pose means nothing
    // moves at all at strength zero, so it begins and ends where it already is.
    for (const held of this.bones.values()) {
      held.bone.quaternion.slerp(held.bent, strength);
    }
  }

  /**
   * Puts a piece of another model on this one.
   *
   * The pack is built so that this works: every character is one skinned mesh
   * split into pieces by material, on the same skeleton with the same bones in
   * the same order. A hat is the part of the witch called Hat, and weighted to
   * the same head bone this body has, so it can simply be bound here and worn.
   *
   * What is asked for is a model and the name of the material the piece is
   * made of: `wear('witch', 'Hat')`.
   */
  async wearPart(model: ModelName, material: string): Promise<boolean> {
    const loaded = await loadModel(model);
    if (this.disposed || !this.skeleton || !this.bindMatrix) return false;

    let found: THREE.SkinnedMesh | null = null;
    loaded.scene.traverse((child) => {
      if (!(child instanceof THREE.SkinnedMesh)) return;

      const worn = child.material as THREE.Material | THREE.Material[];
      const materials = Array.isArray(worn) ? worn : [worn];

      if (materials.some((one) => one.name === material)) found = child;
    });

    if (!found) return false;

    const donor = found as THREE.SkinnedMesh;
    const paint = Array.isArray(donor.material) ? donor.material[0] : donor.material;
    if (!paint) return false;

    const worn = new THREE.SkinnedMesh(donor.geometry, paint.clone());

    worn.scale.setScalar(this.scale);
    worn.bind(this.skeleton, this.bindMatrix);
    worn.frustumCulled = false;

    this.group.add(worn);
    this.parts.push(worn);

    return true;
  }

  /** Takes off everything worn this way. */
  takeOffParts(): void {
    for (const worn of this.parts) {
      worn.removeFromParent();
      (worn.material as THREE.Material).dispose();
    }
    this.parts = [];
  }

  private parts: THREE.SkinnedMesh[] = [];

  /** Whether the body is part way through a bend. */
  get bending(): boolean {
    return this.bendingSince > 0;
  }

  dispose(): void {
    this.disposed = true;
    this.mixer?.stopAllAction();
    this.group.traverse((child) => {
      if (!(child instanceof THREE.Mesh)) return;

      // Geometry is shared with the model this one was cloned from, so only the
      // materials, which are this body's own, are let go here.
      const materials = Array.isArray(child.material)
        ? (child.material as THREE.Material[])
        : [child.material as THREE.Material];
      for (const material of materials) material.dispose();
    });
  }
}
